import type { DashboardFilters as FilterState } from '../hooks/useDashboardStats';
import AutocompleteInput from '@/components/inputs/AutocompleteInput';
import { useDashboardFilters } from '../hooks/useDashboardFilters';
import FilterListIcon from '@mui/icons-material/FilterList';
import SelectInput from '@/components/inputs/SelectInput';
import { useGetAttentionUsersQuery } from '../services';
import DateInput from '@/components/inputs/DateInput';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';

interface DashboardFiltersProps {
  onApply: (filters: FilterState) => void;
}

const SECTOR_OPTIONS = [
  { label: 'CA - Con agenda', value: 'CA' },
  { label: 'CP - Con problemas', value: 'CP' },
  { label: 'SA - Sin agenda', value: 'SA' },
];

const PRESET_OPTIONS = [
  { label: 'Hoy', val: 'today' as const },
  { label: 'Ayer', val: 'yesterday' as const },
  { label: '7 Días', val: 7 as const },
  { label: '30 Días', val: 30 as const },
  { label: '2 Meses', val: '2m' as const },
  { label: '3 Meses', val: '3m' as const },
  { label: '6 Meses', val: '6m' as const },
  { label: '1 Año', val: 'year' as const },
];

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  onApply,
}) => {
  const {
    dateFrom,
    dateTo,
    sectorId,
    userId,
    activePreset,
    setDateFrom,
    setDateTo,
    setSectorId,
    setUserId,
    handlePreset,
    handleApply,
  } = useDashboardFilters(onApply);

  const [searchTerm, setSearchTerm] = useState('');

  const { data: userData, isLoading: isLoadingUsers } =
    useGetAttentionUsersQuery(searchTerm.length >= 3 ? searchTerm : undefined, {
      skip: searchTerm.length > 0 && searchTerm.length < 3,
    });

  const userOptions = useMemo(() => {
    if (!userData) return [];

    return userData.map((user) => ({
      label: user.name,
      data: user.id,
    }));
  }, [userData]);

  const selectedUserOption = useMemo(() => {
    if (!userId) return null;
    return (
      userOptions.find((opt) => opt.data === userId) || {
        label: 'Usuario seleccionado',
        data: userId,
      }
    );
  }, [userId, userOptions]);

  return (
    <div className='bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col gap-4 relative z-20 mb-8'>
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-50 pb-4 mb-2'>
        <div className='flex items-center gap-2 text-slate-400'>
          <FilterListIcon className='!w-5 !h-5 text-orange-500' />
          <span className='text-sm font-bold uppercase tracking-wider'>
            Filtros Avanzados
          </span>
        </div>

        <div className='flex flex-wrap gap-2'>
          {PRESET_OPTIONS.map((p) => (
            <button
              key={p.label}
              onClick={() => handlePreset(p.val)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                activePreset === p.val
                  ? 'bg-[#FF7500] text-white border-[#FF7500]'
                  : 'text-slate-500 bg-slate-50 hover:bg-orange-50 hover:text-orange-600 border-slate-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className='flex flex-col md:flex-row gap-4 items-end'>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 flex-1 w-full'>
          <DateInput
            name='dateFrom'
            placeholder='Desde'
            value={dateFrom}
            onChange={(val) => setDateFrom(val)}
          />
          <DateInput
            name='dateTo'
            placeholder='Hasta'
            value={dateTo}
            onChange={(val) => setDateTo(val)}
          />

          <SelectInput
            name='sector'
            placeholder='Sector'
            value={sectorId}
            options={SECTOR_OPTIONS}
            onChange={setSectorId}
          />

          <AutocompleteInput
            name='user'
            placeholder='Usuario'
            value={selectedUserOption}
            options={userOptions}
            onInputChange={setSearchTerm}
            onChange={(opt) => setUserId(opt ? opt.data : null)}
            disabled={isLoadingUsers}
          />
        </div>

        <button
          onClick={handleApply}
          className='w-full md:w-auto px-6 py-3 bg-[#FF7500] hover:bg-[#e46800] text-white font-bold rounded-lg shadow-md transition-all flex items-center justify-center gap-2 h-[56px]'
        >
          <SearchIcon className='!w-4 !h-4' />
          Aplicar
        </button>
      </div>
    </div>
  );
};
